// src/pages/CohortOverviewPage.js

import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

// Tailwind primary colour
const PRIMARY_COLOR = "#852170";

/**
 * Decide cadence label for a user.
 */
function getCadenceLabel(cadence) {
  if (cadence?.weekly) return "Weekly";
  if (cadence?.biweekly) return "Bi-weekly";
  if (cadence?.monthly) return "Monthly";
  return "Irregular";
}

/**
 * Helper: strip a Date down to its calendar day key (YYYY-MM-DD)
 */
function dayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Build all derived datasets for charts from stats.
 * stats shape: { cohort, users[] } where each user has:
 *  - count
 *  - cadence { weekly, biweekly, monthly }
 *  - timestamps: string[] (ISO)
 *  - uid
 */
function buildChartData(stats) {
  if (!stats || !stats.users || stats.users.length === 0) {
    return {
      cadenceData: null,
      histogramData: null,
      weeklyVolumeData: null,
      weeklyActiveData: null,
      retentionData: null,
      outsideFirstDayHistogramData: null,
    };
  }

  const users = stats.users;

  /* ─────────────────────────────────────────────
   * 1. Cadence distribution
   * ───────────────────────────────────────────── */
  const cadenceBuckets = {
    Weekly: 0,
    "Bi-weekly": 0,
    Monthly: 0,
    Irregular: 0,
  };

  users.forEach((u) => {
    const label = getCadenceLabel(u.cadence);
    cadenceBuckets[label] = (cadenceBuckets[label] || 0) + 1;
  });

  const cadenceLabels = Object.keys(cadenceBuckets);
  const cadenceValues = cadenceLabels.map((k) => cadenceBuckets[k]);

  const cadenceData = {
    labels: cadenceLabels,
    datasets: [
      {
        label: "Patients",
        data: cadenceValues,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
      },
    ],
  };

  /* ─────────────────────────────────────────────
   * 2. Histogram: measurements per patient
   *    + new: “outside first day” histogram
   * ───────────────────────────────────────────── */

  const bins = [
    { label: "0", min: 0, max: 0 },
    { label: "1–2", min: 1, max: 2 },
    { label: "3–5", min: 3, max: 5 },
    { label: "6–10", min: 6, max: 10 },
    { label: "11–20", min: 11, max: 20 },
    { label: "21+", min: 21, max: Infinity },
  ];

  const binCounts = bins.map(() => 0);
  const outsideFirstDayBinCounts = bins.map(() => 0);

  users.forEach((u) => {
    const c = u.count || 0;
    const idx = bins.findIndex((b) => c >= b.min && c <= b.max);
    if (idx >= 0) binCounts[idx] += 1;

    // recordings outside first day
    const ts = (u.timestamps || [])
      .map((iso) => new Date(iso))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    if (ts.length === 0) {
      // 0 extra recordings (only – or no – first day)
      const idx2 = bins.findIndex((b) => 0 >= b.min && 0 <= b.max);
      if (idx2 >= 0) outsideFirstDayBinCounts[idx2] += 1;
    } else {
      const firstDay = dayKey(ts[0]);
      const outsideCount = ts.filter((d) => dayKey(d) !== firstDay).length;
      const idx2 = bins.findIndex(
        (b) => outsideCount >= b.min && outsideCount <= b.max
      );
      if (idx2 >= 0) outsideFirstDayBinCounts[idx2] += 1;
    }
  });

  const histogramData = {
    labels: bins.map((b) => b.label),
    datasets: [
      {
        label: "Patients",
        data: binCounts,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
      },
    ],
  };

  const outsideFirstDayHistogramData = {
    labels: bins.map((b) => b.label),
    datasets: [
      {
        label: "Patients",
        data: outsideFirstDayBinCounts,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
      },
    ],
  };

  /* ─────────────────────────────────────────────
   * 3 & 4. Weekly measurement volume & weekly active patients
   * ───────────────────────────────────────────── */

  const allPoints = [];
  users.forEach((u) => {
    (u.timestamps || []).forEach((iso) => {
      const t = new Date(iso).getTime();
      if (!Number.isNaN(t)) {
        allPoints.push({ uid: u.uid, ts: t });
      }
    });
  });

  if (allPoints.length === 0) {
    return {
      cadenceData,
      histogramData,
      weeklyVolumeData: null,
      weeklyActiveData: null,
      retentionData: null,
      outsideFirstDayHistogramData,
    };
  }

  allPoints.sort((a, b) => a.ts - b.ts);

  const minTs = allPoints[0].ts;
  const maxTs = allPoints[allPoints.length - 1].ts;

  const startOfFirstWeek = new Date(minTs);
  startOfFirstWeek.setHours(0, 0, 0, 0);
  const startTs = startOfFirstWeek.getTime();

  const weekCount = Math.floor((maxTs - startTs) / MS_PER_WEEK) + 1 || 1;

  const weeklyMeasurementCounts = new Array(weekCount).fill(0);
  const weeklyActiveSets = Array.from({ length: weekCount }, () => new Set());

  allPoints.forEach(({ uid, ts }) => {
    const weekIndex = Math.floor((ts - startTs) / MS_PER_WEEK);
    if (weekIndex >= 0 && weekIndex < weekCount) {
      weeklyMeasurementCounts[weekIndex] += 1;
      weeklyActiveSets[weekIndex].add(uid);
    }
  });

  const weeklyActiveCounts = weeklyActiveSets.map((s) => s.size);

  const weekLabels = Array.from({ length: weekCount }, (_, i) => {
    const d = new Date(startTs + i * MS_PER_WEEK);
    return d.toLocaleDateString("en-GB", {
      month: "short",
      day: "numeric",
    });
  });

  const weeklyVolumeData = {
    labels: weekLabels,
    datasets: [
      {
        label: "Measurements per week",
        data: weeklyMeasurementCounts,
        tension: 0.3,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
      },
    ],
  };

  const weeklyActiveData = {
    labels: weekLabels,
    datasets: [
      {
        label: "Active patients per week",
        data: weeklyActiveCounts,
        tension: 0.3,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
      },
    ],
  };

  /* ─────────────────────────────────────────────
   * 5. Retention curve (proportion still active)
   * ───────────────────────────────────────────── */

  const usersWithAny = users.filter((u) => (u.timestamps || []).length > 0);
  const totalMeasuredUsers = usersWithAny.length;

  let retentionData = null;

  if (totalMeasuredUsers > 0) {
    const lastActiveWeekIndexPerUser = usersWithAny.map((u) => {
      const tsList = (u.timestamps || [])
        .map((iso) => new Date(iso).getTime())
        .filter((t) => !Number.isNaN(t))
        .sort((a, b) => a - b);

      const lastTs = tsList[tsList.length - 1];
      return Math.floor((lastTs - startTs) / MS_PER_WEEK);
    });

    const retention = [];
    const retentionLabels = [];

    for (let w = 0; w < weekCount; w++) {
      const stillActive = lastActiveWeekIndexPerUser.filter(
        (idx) => idx >= w
      ).length;
      const proportion = (stillActive / totalMeasuredUsers) * 100;
      retention.push(Number(proportion.toFixed(1)));

      // Use the same calendar week labels as the other charts
      retentionLabels.push(weekLabels[w]);
    }

    retentionData = {
      labels: retentionLabels,
      datasets: [
        {
          label: "% of patients still active",
          data: retention,
          tension: 0.3,
          borderColor: PRIMARY_COLOR,
          backgroundColor: PRIMARY_COLOR + "33",
          pointBackgroundColor: PRIMARY_COLOR,
          pointBorderColor: PRIMARY_COLOR,
        },
      ],
    };
  }

  return {
    cadenceData,
    histogramData,
    weeklyVolumeData,
    weeklyActiveData,
    retentionData,
    outsideFirstDayHistogramData,
  };
}

export default function CohortOverviewPage({ rows }) {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rows || rows.length === 0) return;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const functions = getFunctions(undefined, "europe-west2");
        const cohortStats = httpsCallable(functions, "cohortStats");
        const res = await cohortStats({ patients: rows });
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch cohort stats", err);
        setError(
          err?.message ||
            "Something went wrong while loading cohort statistics."
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [rows]);

  const cohortName = state?.alias || "Cohort";
  const handleBack = () => navigate(-1);

  const {
    cadenceData,
    histogramData,
    weeklyVolumeData,
    weeklyActiveData,
    retentionData,
    outsideFirstDayHistogramData,
  } = useMemo(() => buildChartData(stats), [stats]);

  const commonBarOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        title: {
          display: true,
          text: "Patients", // y-axis label for all histograms
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
  };

  const makeLineOptions = (yLabel, { isPercent = false } = {}) => ({
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        ...(isPercent ? { max: 100 } : {}),
        title: {
          display: true,
          text: yLabel,
        },
        ticks: isPercent
          ? {
              callback: (v) => `${v}%`,
            }
          : {},
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  });

  return (
    <div className="w-full h-full flex flex-col justify-between gap-4 p-4 mx-auto">
      {/* Loading / error states */}
      {loading && (
        <div className="w-full flex justify-center">
          <span className="muted-text">Calculating cohort statistics…</span>
        </div>
      )}

      {error && (
        <div className="w-full border-red-400 border-2">
          <p className="text-red-600 font-semibold mb-1">Error</p>
          <p className="muted-text">{error}</p>
        </div>
      )}

      {!loading && !error && stats && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
            <div className="flex flex-col gap-1 p-2 border border-greyscale-1 rounded-lg">
              <span className="muted-text">Participants</span>
              <span className="subtitle-text">
                {stats.cohort.totalUsersInScope}
              </span>
              <span className="muted-text">
                {stats.cohort.usersWithAnyMeasurements} with ≥1 measurement
              </span>
            </div>

            <div className="flex flex-col gap-1 p-2 border border-greyscale-1 rounded-lg">
              <span className="muted-text">Measurements</span>
              <span className="subtitle-text">
                {stats.cohort.totalMeasurements}
              </span>
              <span className="muted-text">
                {stats.cohort.usersWith3PlusMeasurements} users with ≥3
                measurements
              </span>
            </div>

            <div className="flex flex-col gap-1 p-2 border border-greyscale-1 rounded-lg">
              <span className="muted-text">Recent activity</span>
              <span className="subtitle-text">
                {stats.cohort.usersWithMeasurementLast7Days}
              </span>
              <span className="muted-text">
                users with a measurement in 7 days
              </span>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-ful h-full">
            {/* Weekly measurement volume */}
            <div className="flex flex-col gap-2">
              <span className="subtitle-text">Weekly measurement volume</span>
              <span className="muted-text">
                Number of measurements taken per week.
              </span>
              {weeklyVolumeData ? (
                <Line
                  data={weeklyVolumeData}
                  options={makeLineOptions("Measurements per week")}
                />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show weekly volume.
                </span>
              )}
            </div>

            {/* Weekly active patients */}
            <div className="flex flex-col gap-2">
              <span className="subtitle-text">Weekly active patients</span>
              <span className="muted-text">
                Distinct patients with ≥1 measurement per week.
              </span>
              {weeklyActiveData ? (
                <Line
                  data={weeklyActiveData}
                  options={makeLineOptions("Active patients per week")}
                />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show weekly activity.
                </span>
              )}
            </div>

            {/* Retention */}
            <div className="flex flex-col gap-2 w-full">
              <span className="subtitle-text">Cohort retention</span>
              <span className="muted-text">
                Proportion of patients still measuring at least once per week
                over follow-up.
              </span>
              {retentionData ? (
                <Line
                  data={retentionData}
                  options={makeLineOptions("Active cohort (%)", {
                    isPercent: true,
                  })}
                />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show retention curve.
                </span>
              )}
            </div>

            {/* Cadence distribution */}
            <div className="flex flex-col gap-2">
              <span className="subtitle-text">Cadence distribution</span>
              <span className="muted-text">Patients by adherence pattern.</span>
              {cadenceData ? (
                <Bar data={cadenceData} options={commonBarOptions} />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show cadence distribution.
                </span>
              )}
            </div>

            {/* Histogram: total measurements */}
            <div className="flex flex-col gap-2">
              <span className="subtitle-text">Measurements per patient</span>
              <span className="muted-text">
                Distribution of total measurements per participant.
              </span>
              {histogramData ? (
                <Bar data={histogramData} options={commonBarOptions} />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show histogram.
                </span>
              )}
            </div>

            {/* NEW: histogram of recordings after first day */}
            <div className="flex flex-col gap-2">
              <span className="subtitle-text">
                Recordings after first day per patient
              </span>
              <span className="muted-text">
                How many recordings participants completed on days after their
                first recording.
              </span>
              {outsideFirstDayHistogramData ? (
                <Bar
                  data={outsideFirstDayHistogramData}
                  options={commonBarOptions}
                />
              ) : (
                <span className="muted-text text-xs">
                  Not enough data to show distribution.
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {!loading && !error && !stats && (
        <div className="w-full flex justify-center">
          <span className="muted-text">
            No cohort data available for this view.
          </span>
        </div>
      )}
    </div>
  );
}
